import { Mail, Pencil, Phone, ShieldCheck, UserPlus } from "lucide-react";
import { GUEST, SDTD, STATUS, roleColor } from "@/components/people/roles";

const Chip = ({ color, children, testId }) => (
  <span
    data-testid={testId}
    className="border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider"
    style={{ borderColor: color, color }}
  >
    {children}
  </span>
);

/** One person: who they are, what they do on the team, and whether they are on the portal. */
export const PersonCard = ({ p, here, canEdit, canInvite, showContacts, onEdit, onInvite }) => {
  const [statusLabel, statusColor] = STATUS[p.status] || STATUS.none;
  const others = p.units.filter((u) => u !== here);
  const id = p.card_id || p.account_id || p.key;
  return (
    <div
      className="flex h-full flex-col gap-2 border border-border bg-card p-4 transition-colors duration-200 hover:bg-muted/40"
      style={{ borderTop: `2px solid ${roleColor(p.team_role)}` }}
      data-testid={`person-${id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-sm font-bold" data-testid={`person-name-${id}`}>
            {p.name}
          </div>
          <div className="truncate text-[11px] text-muted-foreground">
            {[p.designation, p.department, p.organisation].filter(Boolean).join(" · ") || "—"}
          </div>
        </div>
        {canEdit && (
          <button
            type="button"
            title="Update"
            data-testid={`person-edit-${id}`}
            onClick={() => onEdit(p)}
            className="shrink-0 border border-border p-1 text-muted-foreground transition-colors duration-200 hover:border-primary hover:text-primary"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <Chip color={roleColor(p.team_role)} testId={`person-role-${id}`}>
          {p.team_role || "Not placed"}
        </Chip>
        {p.team === SDTD && <Chip color="hsl(186 70% 40%)" testId={`person-sdtd-${id}`}>SDTD</Chip>}
        {others.length > 0 && (
          <span title={others.join(", ")}>
            <Chip color="hsl(var(--muted-foreground))" testId={`person-units-${id}`}>
              {here ? "also " : p.team === SDTD ? "covers " : ""}
              {others.length > 3 ? `${others.length} units` : others.join(", ")}
            </Chip>
          </span>
        )}
        <Chip color={statusColor} testId={`person-status-${id}`}>
          {statusLabel}
        </Chip>
        {p.team === GUEST && p.access_label && (
          <Chip color="hsl(var(--primary))" testId={`person-access-${id}`}>
            {p.access_label}
          </Chip>
        )}
        {p.access_override && p.team !== GUEST && (
          <span
            data-testid={`person-access-override-${id}`}
            title="Set by a super-admin, not by the team role"
            className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-primary"
          >
            <ShieldCheck className="h-3 w-3" /> {p.access_label}
          </span>
        )}
      </div>
      {showContacts && (p.email || p.contact_number) && (
        <div className="font-data mt-auto space-y-1 text-[11px] text-muted-foreground">
          {p.email && (
            <a href={`mailto:${p.email}`} className="flex items-center gap-1.5 truncate hover:text-primary">
              <Mail className="h-3 w-3 shrink-0" /> {p.email}
            </a>
          )}
          {p.contact_number && (
            <span className="flex items-center gap-1.5">
              <Phone className="h-3 w-3" /> {p.contact_number}
            </span>
          )}
        </div>
      )}
      {canInvite && p.status === "none" && p.email && p.team_role && (
        <button
          type="button"
          data-testid={`person-invite-${id}`}
          onClick={() => onInvite(p)}
          className="flex w-fit items-center gap-1 border border-primary px-2 py-1 font-display text-[9px] font-bold uppercase tracking-wider text-primary transition-colors duration-200 hover:bg-primary hover:text-primary-foreground"
        >
          <UserPlus className="h-3 w-3" /> Invite to the portal
        </button>
      )}
    </div>
  );
};
